import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { getExchangeRate } from "@/utils/exchangeRates";

interface AddExpenseDialogProps {
  organizationId: string;
}

const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "CAD", symbol: "CA$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
];

const AddExpenseDialog = ({ organizationId }: AddExpenseDialogProps) => {
  const [open, setOpen] = useState(false);
  const [categoryId, setCategoryId] = useState<string>("");
  const [currency, setCurrency] = useState("USD");
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: categories } = useQuery({
    queryKey: ["categories", organizationId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("organization_id", organizationId)
        .order("name");
      
      if (error) throw error;
      return data;
    },
  });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!organizationId) {
      console.error("No organization ID provided");
      toast({
        title: "Error",
        description: "Please select an organization first",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      console.log("Starting form submission...");

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error("Session error:", sessionError);
        toast({
          title: "Authentication Error",
          description: "Please login to continue",
          variant: "destructive",
        });
        navigate("/login");
        return;
      }

      if (!session) {
        console.log("No active session found");
        toast({
          title: "Please Login",
          description: "You need to be logged in to create expenses",
          variant: "destructive",
        });
        navigate("/login");
        return;
      }

      if (!categoryId) {
        console.error("No category selected");
        toast({
          title: "Error",
          description: "Please select a category",
          variant: "destructive",
        });
        return;
      }

      const form = e.target as HTMLFormElement;
      const formData = new FormData(form);
      
      const amount = formData.get('amount') as string;
      const description = formData.get('description') as string;
      const date = formData.get('date') as string;
      const remarks = formData.get('remarks') as string;

      console.log("Form data collected:", { amount, description, date, remarks, categoryId, currency });

      if (!amount || !description) {
        console.error("Missing required fields");
        toast({
          title: "Error",
          description: "Please fill in all required fields",
          variant: "destructive",
        });
        return;
      }

      // Get organization's base currency
      const { data: org } = await supabase
        .from("organizations")
        .select("currency")
        .eq("id", organizationId)
        .single();

      const baseCurrency = org?.currency || 'USD';
      let exchangeRate = null;

      // Only get exchange rate if currencies are different
      if (currency !== baseCurrency) {
        try {
          exchangeRate = await getExchangeRate(currency, baseCurrency);
          console.log(`Got exchange rate from ${currency} to ${baseCurrency}: ${exchangeRate}`);
        } catch (error) {
          console.error("Error getting exchange rate:", error);
          // Continue without exchange rate
        }
      }

      const expenseData = {
        organization_id: organizationId,
        amount: Number(amount),
        currency: currency,
        category_id: parseInt(categoryId),
        description: String(description),
        remarks: remarks ? String(remarks) : null,
        date: date ? String(date) : new Date().toISOString().split('T')[0],
        user_id: session.user.id,
        exchange_rate: exchangeRate,
        base_currency: exchangeRate ? baseCurrency : null
      };

      console.log("Submitting expense data:", expenseData);

      const { error: insertError } = await supabase
        .from("expenses")
        .insert([expenseData]);

      if (insertError) {
        console.error("Error inserting expense:", insertError);
        throw insertError;
      }

      console.log("Expense created successfully");
      
      toast({
        title: "Success",
        description: "Expense added successfully",
      });

      form.reset();
      setCategoryId("");
      setCurrency("USD");
      setOpen(false);
      
      await queryClient.invalidateQueries({ queryKey: ["expenses"] });
      
    } catch (error: any) {
      console.error("Error creating expense:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to create expense",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="gap-2">
          <Plus className="h-4 w-4" /> Create Expense
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-[425px] sm:w-full p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Add New Expense</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-1">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  type="number"
                  id="amount"
                  name="amount"
                  placeholder="0.00"
                  step="0.01"
                  required
                  className="mt-1"
                />
              </div>
              <div className="col-span-1">
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={currency}
                  onValueChange={setCurrency}
                  name="currency"
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent>
                    {CURRENCIES.map((curr) => (
                      <SelectItem key={curr.code} value={curr.code}>
                        {curr.code} ({curr.symbol})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="category">Category</Label>
              <Select
                value={categoryId}
                onValueChange={setCategoryId}
                required
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories?.map((category) => (
                    <SelectItem 
                      key={category.id} 
                      value={String(category.id)}
                      className="flex items-center gap-2"
                    >
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: category.color }}
                      />
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Input
                type="text"
                id="description"
                name="description"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                type="date"
                id="date"
                name="date"
                defaultValue={new Date().toISOString().split('T')[0]}
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea
                id="remarks"
                name="remarks"
                className="mt-1"
              />
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 sm:gap-4 mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full sm:w-auto"
            >
              {loading ? "Adding..." : "Add Expense"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddExpenseDialog;
